import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import { trans, navigate, safeRoute, renderThreadSkeleton, renderLoadMore, renderEmpty } from '../utils';
import { bookmarksEnabled, usesFofBookmarks, bookmarksPageTitle, bookmarksRouteName, fofTrans, BOOKMARKED_FILTER_KEY } from '../utils/bookmarks';
import { toggleDiscussionLike } from '../utils/likes';
import { DISCUSSION_LIST_SORT } from '../utils/sortOptions';
import DiscussionFeedState from '../states/DiscussionFeedState';
import BookmarkedPostListState from '../states/BookmarkedPostListState';
import ThreadCard from './shared/ThreadCard';
import SortDropdown from './shared/SortDropdown';
import PostCard from './shared/PostCard';
/**
 * Página "Salvos" — a do tema (`/bookmarks`) e, quando o fof/bookmarks está
 * ativo, a dele: index.tsx troca só o componente da rota `fof-bookmarks`, para
 * que a listagem apareça em ThreadCard como no resto do tema em vez do
 * DiscussionListItem do core.
 *
 * As discussões saem da busca com o filtro do provedor no comando
 * (`avocadoBookmarked` ou o `bookmarked` do fof), então paginação, ordenação e
 * card vêm de graça. A aba "Posts" só existe cedendo ao fof — marcar posts é
 * recurso dele. Guests recebem um convite de login: o item de menu só aparece
 * logado, mas a URL é alcançável direto.
 */
export default class BookmarksPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "feedState", void 0);
    _defineProperty(this, "postsState", void 0);
    _defineProperty(this, "tab", 'discussions');
    _defineProperty(this, "likingIds", new Set());
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'App--index';

    // Sistema desligado no admin: a rota PHP continua existindo, então quem
    // chega por URL direta volta para a home em vez de ver uma página morta.
    if (!bookmarksEnabled()) {
      m.route.set('/');
      return;
    }

    // Define o título no oninit (antes do primeiro render), como o TeamPage —
    // setar no oncreate deixava a aba com o título da página anterior.
    app.setTitle(bookmarksPageTitle());
    app.setTitleCount(0);
    this.tab = this.tabFromRoute();
    if (app.session.user) {
      const sort = m.route.param('sort') || 'latest';
      const filterKey = usesFofBookmarks() ? 'bookmarked' : BOOKMARKED_FILTER_KEY;
      this.feedState = new DiscussionFeedState({
        sort,
        filter: {
          [filterKey]: true
        }
      });
      this.loadCurrentTab();
    }
  }

  /**
   * Trocar de aba é uma mudança de query string na mesma rota, e o Mithril
   * reaproveita a instância sem passar de novo pelo oninit — a aba tem que ser
   * relida a cada draw.
   */
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const current = this.tabFromRoute();
    if (current === this.tab) return;
    this.tab = current;
    this.loadCurrentTab();
  }
  view() {
    const homeHref = safeRoute('index');
    const showsTabs = usesFofBookmarks();
    return m("div", {
      className: "AvocadoDiscussions AvocadoBookmarks"
    }, m("div", {
      className: "AvocadoNav-helper"
    }, m(IndexSidebar, null)), m("div", {
      className: "AvocadoDiscussions-header"
    }, m("h1", {
      className: "AvocadoDiscussions-title"
    }, m("i", {
      className: "fas fa-bookmark AvocadoBookmarks-titleIcon",
      "aria-hidden": "true"
    }), bookmarksPageTitle()), app.session.user && m("div", {
      className: "AvocadoDiscussions-controls"
    }, this.tab === 'discussions' && m(SortDropdown, {
      options: DISCUSSION_LIST_SORT,
      currentKey: this.feedState.getParams().sort || 'latest',
      onChange: key => this.feedState.refreshParams({
        sort: key
      }, 1)
    }), m("a", {
      className: "AvocadoDiscussions-homeLink",
      href: homeHref,
      onclick: e => navigate(e, homeHref)
    }, m("i", {
      className: "fas fa-arrow-left",
      "aria-hidden": "true"
    }), trans('ramon-avocado.forum.discussions.home', 'Home')))), app.session.user && showsTabs && this.renderTabs(), !app.session.user ? this.renderGuestPrompt() : this.renderCurrentTab());
  }
  tabFromRoute() {
    return usesFofBookmarks() && m.route.param('tab') === 'posts' ? 'posts' : 'discussions';
  }

  /** Cada lista só é buscada quando a aba é aberta pela primeira vez. */
  loadCurrentTab() {
    if (this.tab === 'discussions') {
      if (!this.feedState.hasItems()) this.feedState.refresh();
      return;
    }
    if (!this.postsState) this.postsState = new BookmarkedPostListState();
    if (this.postsState.isInitialLoading() || this.postsState.hasItems()) return;
    this.postsState.refresh().then(() => m.redraw());
  }
  renderTabs() {
    const tabs = [{
      key: 'discussions',
      icon: 'far fa-comments',
      label: fofTrans('page.tab.discussions', 'Discussions')
    }, {
      key: 'posts',
      icon: 'far fa-comment',
      label: fofTrans('page.tab.posts', 'Posts')
    }];
    return m("div", {
      className: "AvocadoBookmarks-tabs",
      role: "tablist"
    }, tabs.map(tab => {
      const active = this.tab === tab.key;
      const href = app.route(bookmarksRouteName(), tab.key === 'discussions' ? {} : {
        tab: tab.key
      });
      return m("a", {
        key: tab.key,
        className: `AvocadoBookmarks-tab${active ? ' is-active' : ''}`,
        href: href,
        role: "tab",
        "aria-selected": active,
        onclick: e => navigate(e, href)
      }, m("i", {
        className: tab.icon,
        "aria-hidden": "true"
      }), tab.label);
    }));
  }
  renderCurrentTab() {
    return this.tab === 'posts' ? this.renderPosts() : this.renderDiscussions();
  }

  /**
   * Reusa o PostCard compartilhado (o mesmo do perfil e da busca), na mesma
   * pilha da aba de discussões. A PostList do core desenhava o post inteiro —
   * imagem em tamanho original inclusive — e destoava de tudo à volta.
   */
  renderPosts() {
    const state = this.postsState;
    const posts = state?.getPages().flatMap(page => page.items) || [];
    const isLoading = !state || state.isInitialLoading() || state.isLoadingNext();
    if (posts.length === 0) {
      return m("div", {
        className: "AvocadoHome-threadStack"
      }, isLoading ? renderThreadSkeleton(5) : renderEmpty(trans('ramon-avocado.forum.bookmarks.empty_posts', "You haven't bookmarked any posts yet.")));
    }
    return m('[', null, m("div", {
      className: "AvocadoHome-threadStack"
    }, posts.map(post => m(PostCard, {
      key: post.id?.(),
      post: post,
      context: this,
      showBadges: false
    })), isLoading && renderThreadSkeleton(3)), !isLoading && state.hasNext() && renderLoadMore(trans('ramon-avocado.forum.discussions.load_more', 'Load more'), () => state.loadNext()));
  }
  renderGuestPrompt() {
    return m("div", {
      className: "AvocadoBookmarks-guest"
    }, m("i", {
      className: "far fa-bookmark",
      "aria-hidden": "true"
    }), m("p", null, trans('ramon-avocado.forum.bookmarks.guest_prompt', 'Log in to save discussions and find them here later.')), m("button", {
      className: "Button Button--primary",
      type: "button",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
    }, trans('ramon-avocado.forum.home.log_in', 'Log In')));
  }
  renderDiscussions() {
    const discussions = this.feedState.flatItems();
    const isLoadingNext = this.feedState.isLoadingNext();
    const isInitialLoading = this.feedState.isInitialLoading();
    return m('[', null, m("div", {
      className: "AvocadoHome-threadStack"
    }, discussions.length === 0 && isInitialLoading ? renderThreadSkeleton(5) : discussions.length === 0 ? renderEmpty(trans('ramon-avocado.forum.bookmarks.empty', "You haven't saved any discussions yet.")) : discussions.map(d => m(ThreadCard, {
      key: d.id?.(),
      discussion: d,
      context: this,
      likingIds: this.likingIds,
      showBookmarkMeta: true,
      onToggleLike: disc => toggleDiscussionLike(disc, this.likingIds)
    })), discussions.length > 0 && isLoadingNext && renderThreadSkeleton(3)), this.feedState.hasNext() && !isLoadingNext && renderLoadMore(trans('ramon-avocado.forum.discussions.load_more', 'Load more'), () => this.feedState.loadNext()));
  }
}
flarum.reg.add('ramon-avocado', 'forum/components/BookmarksPage', BookmarksPage);